import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogContent,
  MatDialogRef
} from "@angular/material/dialog";
import { ResourceStore } from "@app/services/store/resource-store";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { Ticket, Ticket_DetailProjection } from "@app/models/rest/ticket";
import { Router, RouterLink } from "@angular/router";
import moment from "moment";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import { CommentModel } from "@app/models/rest/comment-model";
import { CommentStore } from "@app/services/store/comment-store";
import { CommentType } from "@app/models/enums/comment-type.enum";
import { VisitStatus } from "@app/models/enums/visit-status.enum";
import { AddVisitDialogComponent } from "@app/view/jobs/add-visit-dialog/add-visit-dialog.component";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { TicketStatus } from "@app/models/enums/ticket-status.enum";
import { TicketDurationDialogComponent } from "@app/view/planning/ticket-duration-dialog/ticket-duration-dialog.component";
import { Visit, Visit_DetailProjection } from "@app/models/rest/visit";
import { VisitStore } from "@app/services/store/visit-store";
import { AvailabilityVisitService } from "@app/services/http/availability-visit.service";
import { VisitBookingButtonComponent } from "@app/view/shared/visit-booking-button/visit-booking-button.component";
import {
  ClientConfigService,
  TicketColoursConfig
} from "@app/core/config/client-config.service";
import { TicketCompletionUtilityComponent } from "@app/view/planning/ticket-completion-utility/ticket-completion-utility.component";
import { ResourceCollection } from "@lagoshny/ngx-hateoas-client";
import { TicketAuditViewerComponent } from "@app/view/shared/audits/ticket-audit-viewer/ticket-audit-viewer.component";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { JobStatus } from "@app/models/enums/job-status.enum";
import { ResourceRuleStore } from "@app/services/store/resource-rule-store";
import { ResourceModel } from "@app/models/rest/resource";
import { VisitTicketEditorComponent } from "@app/view/planning/visit-ticket-editor/visit-ticket-editor.component";
import { PlannerNoteEditDialogComponent } from "@app/view/planning/planner-note-edit-dialog/planner-note-edit-dialog.component";
import { CdkDrag } from "@angular/cdk/drag-drop";
import { ClipboardModule } from "@angular/cdk/clipboard";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import {
  MatList,
  MatListItem,
  MatListSubheaderCssMatStyler
} from "@angular/material/list";
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperIcon
} from "@angular/material/stepper";
import { JobDescriptionWidgetComponent } from "../../../integrations/urm/views/job-description-widget/job-description-widget.component";
import { MatLine } from "@angular/material/core";
import { PropertyInfoComponent } from "../../../integrations/urm/views/property-info/property-info.component";
import { JobContactWidgetComponent } from "../../../integrations/urm/views/job-contact-widget/job-contact-widget.component";
import { MomentModule } from "ngx-moment";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";
import { MatChip, MatChipSet } from "@angular/material/chips";
import { TicketEditDialogComponent } from "@app/view/planning/ticket-edit-dialog/ticket-edit-dialog.component";
import { TicketRecallDialogComponent } from "@app/view/planning/ticket-recall-dialog/ticket-recall-dialog.component";
import { TicketRecallService } from "@app/services/http/ticket-recall.service";
import { CautionWidgetComponent } from "@app/integrations/urm/views/caution-widget/caution-widget.component";
import { CoreLaunchWidgetComponent } from "@app/integrations/urm/views/core-launch-widget/core-launch-widget.component";
import { StompRouterService } from "@app/services/webstomp/stomp-router.service";
import { SolverService } from "@app/services/rest/solver.service";
import { NewSolutionEvent } from "@app/models/dto/new-solution-event";
import { merge, Subscription } from "rxjs";
import { debounceTime, filter, map, skip } from "rxjs/operators";

@Component({
  selector: "app-ticket-action-dialog",
  templateUrl: "./ticket-action-dialog.component.html",
  styleUrls: ["./ticket-action-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDrag,
    ClipboardModule,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatButton,
    RouterLink,
    MatDialogContent,
    MatTabGroup,
    MatTab,
    MatList,
    MatListSubheaderCssMatStyler,
    MatListItem,
    FormsModule,
    ReactiveFormsModule,
    MatStepper,
    MatStep,
    MatStepLabel,
    MatStepperIcon,
    JobDescriptionWidgetComponent,
    MatLine,
    PropertyInfoComponent,
    JobContactWidgetComponent,
    MomentModule,
    DialogHeaderComponent,
    MatChipSet,
    MatChip,
    CautionWidgetComponent,
    CoreLaunchWidgetComponent,
    VisitBookingButtonComponent
  ]
})
export class TicketActionDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<TicketActionDialogComponent>>(MatDialogRef);
  ticketStore = inject(TicketStreamingStore);
  private stompRouter = inject(StompRouterService);
  private solverService = inject(SolverService);
  visitStore = inject(VisitStore);
  resourceStore = inject(ResourceStore);
  resourceRuleStore = inject(ResourceRuleStore);
  protected dialog = inject(MatDialog);
  protected formBuilder = inject(UntypedFormBuilder);
  commentStore = inject(CommentStore);
  router = inject(Router);
  snackBar = inject(MatSnackBar);
  availabilityService = inject(AvailabilityVisitService);
  serviceErrorHandler = inject(ServiceErrorHandlerService);
  clientConfigService = inject(ClientConfigService);
  protected store = inject<Store<any>>(Store);
  private recallService = inject(TicketRecallService);
  private cdr = inject(ChangeDetectorRef);
  data = inject<{
    ticketId: number;
  }>(MAT_DIALOG_DATA);

  loading: boolean = false;
  editMode: boolean = false;
  saving: boolean = false;
  ticket: Ticket_DetailProjection;
  currentComment: CommentModel = null;
  appointmentText: string = "";
  loadingComment: boolean = true;
  plannerNoteForm: UntypedFormGroup;
  isProject: boolean = false;
  followOnComment: string = null;
  followOnResource: string = null;
  operativeNote: string = null;
  operativeNoteResource: string = null;
  isDespatched: boolean = false;
  isEnRoute: boolean = false;
  isArrived: boolean = false;
  isStarted: boolean = false;
  isCompleted: boolean = false;
  priorityBackground: string = "";
  priorityForeground: string = "";
  public allowAudit: boolean = false;
  public allowForceBooking: boolean = false;
  forbiddenList: string[] = [];
  schedulesExpanded: boolean = false;
  /**
   * Number of tickets attached to this ticket's visit. A visit with more than
   * one ticket indicates two-person (or multi-resource) working and is used to
   * gate recall when `disableTwoPersonTicketRecall` is enabled. This is loaded
   * live from the visit's ticketList rather than inferred from
   * `job.minimumAttendance` (which is a safety flag, not a ticket count).
   */
  visitTicketCount: number = 1;
  protected readonly JobStatus = JobStatus;
  private _colours: TicketColoursConfig;

  /**
   * Quiet period applied to the STOMP entity streams before reloading. A booking change
   * fans out into several ticket/visit messages; one debounced reload covers the burst.
   */
  private static readonly LIVE_RELOAD_DEBOUNCE_MS = 300;

  private readonly subscriptions = new Subscription();

  constructor() {
    const router = this.router;

    this.subscriptions.add(
      router.events.subscribe(() => {
        this.dialogRef.close();
      })
    );

    this.plannerNoteForm = this.formBuilder.group({
      noteContent: [null, [Validators.required]]
    });

    this._colours = this.clientConfigService.config.ticketColours;

    this.subscriptions.add(
      this.store.pipe(select(selectorAuth)).subscribe((auth) => {
        this.allowAudit = !!auth && auth.allowAudit;
        this.allowForceBooking = !!auth && auth.allowForceBooking;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnInit() {
    this.subscribeToLiveUpdates();
    this.reload();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Reload the dialog when the displayed ticket (or its visit) changes on the server —
   * whether that change came from one of this dialog's own sub-dialogs, from another
   * user booking/rebooking/re-planning elsewhere, or from the SOLVER reassigning this
   * ticket to a different operative. User/operative changes arrive on the Ticket/Visit
   * entity STOMP streams; solver moves are suppressed on those streams while a ticket is
   * PLANNING (see TicketChangeListener) and instead arrive via the solver status stream
   * as a NewSolutionEvent. The payloads are change signals only (raw projection JSON
   * without HATEOAS relations / partial DTOs), so we re-fetch through the normal REST path
   * (the same idiom the schedule viewer uses).
   */
  private subscribeToLiveUpdates() {
    const ticketChanges = this.stompRouter.getEntityStream("Ticket").pipe(
      // skip(1): entity streams are BehaviorSubjects and replay their most recent
      // (pre-dialog, stale) event on subscribe.
      skip(1),
      filter(
        (event) =>
          !!event &&
          (event.id === this.data.ticketId ||
            event.payload?.id === this.data.ticketId)
      )
    );
    const visitChanges = this.stompRouter.getEntityStream("Visit").pipe(
      skip(1),
      filter(
        (event) =>
          !!event &&
          this.ticket?.visit?.id != null &&
          (event.id === this.ticket.visit.id ||
            event.payload?.id === this.ticket.visit.id)
      )
    );
    const solverChanges = this.solverService.updateStream.pipe(
      map((info) => info?.changeEvent),
      filter(
        (event): event is NewSolutionEvent =>
          !!event && this.solverEventTouchesThisTicket(event)
      )
    );
    this.subscriptions.add(
      merge(ticketChanges, visitChanges, solverChanges)
        .pipe(debounceTime(TicketActionDialogComponent.LIVE_RELOAD_DEBOUNCE_MS))
        .subscribe(() => {
          // Don't clobber an in-progress planner-note edit with a live reload. The
          // user's unsaved text wins; saving (or closing and reopening) picks up the
          // external change. Sub-dialog reloads call reload() directly and never
          // coincide with inline note editing, so this guard is live-path only.
          if (this.editMode || this.saving) {
            return;
          }
          this.reload();
        })
    );
  }

  /**
   * True when a solver solution delta reassigned, added or removed the ticket this dialog
   * is showing (matched by {@code ticketId}).
   */
  private solverEventTouchesThisTicket(event: NewSolutionEvent): boolean {
    return [
      ...(event.added ?? []),
      ...(event.updated ?? []),
      ...(event.removed ?? [])
    ].some((dto) => dto.ticketId === this.data.ticketId);
  }

  reload() {
    // Repopulated by the resource-rule search below; reset so repeated reloads
    // cannot accumulate duplicate names.
    this.forbiddenList = [];
    this.loadingComment = true;
    this.ticketStore
      .loadSingleProjection<Ticket_DetailProjection>(
        Ticket_DetailProjection,
        this.data.ticketId,
        "detail"
      )
      .then((ticket) => {
        this.ticket = ticket;
        // console.log("ticket loaded");

        this.refreshTicketCalculations();
        this.priorityBackground = ticket?.visit?.job?.priority?.backgroundColor;
        this.priorityForeground = ticket?.visit?.job?.priority?.foregroundColor;

        if (ticket?.visit?.appointmentStart) {
          if (moment(ticket.visit.appointmentStart).isSame(moment(), "day")) {
            this.appointmentText = `${moment(
              ticket.visit.appointmentStart
            ).format("HH:mm")} - ${moment(ticket.visit.appointmentEnd).format(
              "HH:mm"
            )} Today`;
          } else {
            this.appointmentText = `${moment(
              ticket.visit.appointmentStart
            ).format("DD/MM/YYYY HH:mm")} - ${moment(
              ticket.visit.appointmentEnd
            ).format("HH:mm")}`;
          }
        } else {
          this.appointmentText = "No appointment booked";
        }
        if (ticket?.visit?.job?.externalProjectReference) {
          this.isProject = true;
        }
        this.cdr.markForCheck();
        ticket
          .getRelatedCollection<ResourceCollection<CommentModel>>("comments")
          .subscribe((value) => {
            // console.log("got rel comments");
            // console.log(value);
            this.loadingComment = false;
            value.resources.forEach((comment) => {
              switch (comment.commentType) {
                case CommentType.PlannerNote:
                  this.currentComment = comment;
                  this.plannerNoteForm
                    .get("noteContent")
                    .setValue(comment.content);
                  break;
                case CommentType.FollowOnNote:
                  this.followOnComment = comment.content;
                  this.followOnResource =
                    this.ticket.resourceAvailability.resource.name;
                  break;
                case CommentType.OperativeNote:
                  this.operativeNote = comment.content;
                  this.operativeNoteResource =
                    this.ticket.resourceAvailability.resource.name;
                  break;
              }
            });
            this.cdr.markForCheck();
          });
        ticket.getRelation<Visit>("visit").subscribe((storedVisit) => {
          this.ticket.visit.visitStatus = storedVisit.visitStatus;
          this.ticket.visit.appointmentStart = storedVisit.appointmentStart;
          this.ticket.visit.appointmentEnd = storedVisit.appointmentEnd;
          this.cdr.markForCheck();
        });
        this.loadVisitTicketCount(ticket.visit.id);
        this.resourceRuleStore
          .advancedSearchObservable({}, [
            {
              key: "ruleValue",
              operation: "Contains",
              value: ticket.visit.job.externalPropertyReference
            }
          ])
          .subscribe((value) => {
            console.log("got rules", value);
            if (!value || !value.resources) return;
            value.resources.forEach((value1) => {
              value1
                .getRelation("resource")
                .subscribe((value2: ResourceModel) => {
                  console.log("resource", value2);
                  this.forbiddenList.push(value2.name);
                  this.cdr.markForCheck();
                });
            });
          });
      });
  }

  refreshTicketCalculations() {
    // Live reloads can move a ticket backwards (e.g. a recall), so clear the
    // timeline flags before re-deriving them.
    this.isCompleted = false;
    this.isStarted = false;
    this.isArrived = false;
    this.isEnRoute = false;
    this.isDespatched = false;
    if (this.ticket.ticketStatus === TicketStatus.COMPLETE) {
      this.isCompleted = true;
      this.isStarted = true;
      this.isArrived = true;
      this.isEnRoute = true;
      this.isDespatched = true;
    } else if (this.ticket.ticketStatus === TicketStatus.STARTED) {
      this.isStarted = true;
      this.isArrived = true;
      this.isEnRoute = true;
      this.isDespatched = true;
    } else if (this.ticket.ticketStatus === TicketStatus.ARRIVED) {
      this.isArrived = true;
      this.isEnRoute = true;
      this.isDespatched = true;
    } else if (this.ticket.ticketStatus === TicketStatus.ENROUTE) {
      this.isEnRoute = true;
      this.isDespatched = true;
    } else if (this.ticket.ticketStatus === TicketStatus.DESPATCHED) {
      this.isDespatched = true;
    }
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onConfirmClick() {}

  saveNote() {
    if (this.currentComment === null) {
      this.editMode = false;
      this.saving = true;
      this.cdr.markForCheck();
      // console.log("saving content", this.plannerNoteForm.controls["noteContent"].value);
      const commentModel = new CommentModel();
      commentModel.commentType = CommentType.PlannerNote;
      commentModel.content =
        this.plannerNoteForm.controls["noteContent"].value?.trim() || "";
      this.commentStore.create(commentModel).subscribe((value) => {
        const savedModel = value as CommentModel;
        // console.log("saved", savedModel);
        this.ticketStore.loadSingle(this.ticket.id).then((savedTicket) => {
          savedTicket
            .addCollectionRelation("comments", [savedModel])
            .subscribe(() => {
              // console.log("done");
              this.saving = false;
              this.editMode = false;
              this.currentComment = savedModel;
              this.cdr.markForCheck();
            });
        });
      });
    } else {
      this.currentComment.content =
        this.plannerNoteForm.controls["noteContent"].value?.trim() || "";
      this.commentStore.update(this.currentComment).subscribe(() => {
        this.saving = false;
        this.editMode = false;
        this.cdr.markForCheck();
      });
    }
  }

  createFollowOn() {
    this.ticketStore
      .loadSingleProjection<Ticket_DetailProjection>(
        Ticket_DetailProjection,
        this.ticket.id,
        "detail"
      )
      .then((storedTicket) => {
        let matDialogRef = this.dialog.open(AddVisitDialogComponent, {
          data: {
            job: storedTicket.visit.job,
            visit: storedTicket.visit,
            actionTicketId: storedTicket.id
          },
          width: "500px"
        });
        matDialogRef.afterClosed().subscribe((value) => {
          // console.log("closed");

          if (value) {
            this.loading = true;
            this.cdr.markForCheck();
            this.clearFollowOn();
          }
        });
      })
      .catch((reason) => {
        this.serviceErrorHandler.handleError(reason);
      });
  }

  clearFollowOn() {
    this.ticketStore.loadSingle(this.ticket.id).then((storedTicket: Ticket) => {
      storedTicket.followOnActioned = true;
      this.ticketStore.update(storedTicket).subscribe(() => {
        this.dialogRef.close();
      });
    });
  }

  onCopied() {
    this.snackBar.open("Copied to clipboard!", "Close", {
      duration: 1500,
      horizontalPosition: "right",
      verticalPosition: "top"
    });
  }

  alterDuration() {
    let durationDialog = this.dialog.open(TicketDurationDialogComponent, {
      data: { ticket: this.ticket },
      width: "500px"
    });
    durationDialog.afterClosed().subscribe(() => {
      this.reload();
    });
  }

  clearForcedStart() {
    this.ticketStore.loadSingle(this.ticket.id).then((storedTicket: Ticket) => {
      storedTicket.forceStartTime = null;
      this.ticketStore.update(storedTicket).subscribe(() => {
        this.reload();
      });
    });
  }

  clearLockResource() {
    if (this.ticket.lockToResource) {
      this.resourceStore
        .loadSingle(this.ticket.lockToResource.id)
        .then((resource) => {
          this.ticketStore
            .loadSingle(this.ticket.id)
            .then((storedTicket: Ticket) => {
              storedTicket
                .deleteRelation("lockToResource", resource)
                .subscribe(() => {
                  this.reload();
                });
            });
        });
    }
  }

  formatSeconds(waitingTime: number) {
    return Math.round(waitingTime / 60) + " minutes";
  }

  forceCompletion() {
    this.dialog.open(TicketCompletionUtilityComponent, {
      data: {
        ticket: this.ticket
      }
    });
  }

  recallTicket() {
    const recallRef = this.dialog.open(TicketRecallDialogComponent);
    recallRef.afterClosed().subscribe((result) => {
      if (result) {
        this.recallService.initiateRecall(this.ticket.id).then(() => {
          this.snackBar.open("Recall initiated successfully", "Close", {
            duration: 5000
          });
          this.reload();
        });
      }
    });
  }

  get isRecallAllowed(): boolean {
    const config = this.clientConfigService.config;
    if (config.disableTicketRecall) {
      return false;
    }
    // Two-person working is determined by the number of tickets attached to the
    // visit, NOT by `job.minimumAttendance` (which only flags jobs that
    // *require* a second person for safety). A visit can have multiple tickets
    // added manually or via later amendment even when minimumAttendance is 1.
    if (config.disableTwoPersonTicketRecall && this.visitTicketCount > 1) {
      return false;
    }
    return true;
  }

  /**
   * Loads the visit's full ticket list and records the count so the recall UI
   * can react to multi-resource visits regardless of when additional tickets
   * were added.
   */
  private loadVisitTicketCount(visitId: number) {
    this.visitStore
      .loadSingleProjection<Visit_DetailProjection>(
        Visit_DetailProjection,
        visitId,
        "detail"
      )
      .then((visit) => {
        this.visitTicketCount = visit?.ticketList?.length ?? 1;
        this.cdr.markForCheck();
      })
      .catch((reason) => {
        console.warn("Failed to load visit ticket count", reason);
      });
  }

  openAudit() {
    this.dialog.open(TicketAuditViewerComponent, {
      width: "calc(100vw - 100px)",
      data: {
        ticketId: this.ticket.id
      }
    });
  }

  // Helper method to calculate days between two dates for floating bookings
  getDaysRange(): number {
    if (
      !this.ticket ||
      !this.ticket.visit.appointmentStart ||
      !this.ticket.visit.appointmentEnd
    ) {
      return 0;
    }

    const startDate = moment(this.ticket.visit.appointmentStart);
    const endDate = moment(this.ticket.visit.appointmentEnd);

    // Add 1 to include both start and end days
    return endDate.diff(startDate, "days") + 1;
  }

  amendRequirements() {
    this.dialog.open(VisitTicketEditorComponent, {
      width: "460px",
      data: {
        visitId: this.ticket.visit.id
      }
    });
  }

  canAmend() {
    return (
      !this.isProject &&
      (this.ticket.visit.visitStatus === VisitStatus.NEW ||
        this.ticket.visit.visitStatus === VisitStatus.BOOKED ||
        this.ticket.visit.visitStatus === VisitStatus.FLOATING ||
        this.ticket.visit.visitStatus === VisitStatus.REFERRED)
    );
  }

  canAmendDuration() {
    return (
      !this.isProject && this.ticket.visit.visitStatus !== VisitStatus.CANCELLED
    );
  }

  getAmendRestrictionReason() {
    if (this.isProject) {
      return " Amend in Orchestrator";
    } else if (
      ![
        VisitStatus.NEW,
        VisitStatus.BOOKED,
        VisitStatus.FLOATING,
        VisitStatus.REFERRED
      ].includes(this.ticket.visit.visitStatus)
    ) {
      return `Visit status is ${this.ticket.visit.visitStatus}`;
    }
    return null;
  }

  openPlannerNoteDialog() {
    const dialogRef = this.dialog.open(PlannerNoteEditDialogComponent, {
      width: "500px",
      data: {
        currentNote: this.currentComment?.content || "",
        ticketReference: this.ticket.visit.job.externalJobReference
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.plannerNoteForm.get("noteContent")?.setValue(result.noteContent);
        this.saveNote();
      }
    });
  }

  openManualPlanningDialog() {
    const dialogRef = this.dialog.open(TicketEditDialogComponent, {
      width: "400px",
      data: {
        ticketId: this.ticket.id,
        newForceStartTime: this.ticket.startedTime,
        oldResourceId: this.ticket.resourceAvailability.resource.id,
        newResourceId: this.ticket.resourceAvailability.resource.id
      },
      panelClass: "toolbar-popup-dialog"
    });
    dialogRef.afterClosed().subscribe((result) => {
      this.reload();
    });
  }

  copyTitleToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.snackBar.open(`Copied "${text}" to clipboard!`, "Close", {
          duration: 2000,
          horizontalPosition: "right",
          verticalPosition: "top"
        });
      })
      .catch(() => {
        this.snackBar.open("Failed to copy to clipboard", "Close", {
          duration: 2000,
          horizontalPosition: "right",
          verticalPosition: "top"
        });
      });
  }
}
